import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { House, ClipboardList, Sparkles, Ellipsis, SquarePlus } from 'lucide-react';
import { useStudioNavGroups } from 'components/global/LeftSidebarContent';
import { useCreateActions } from 'components/global/HeaderCreateMenu';
import { M } from './theme';

/**
 * Route tabs come from the shared studio nav, so they're permission-gated;
 * unreachable tabs are omitted. The current tab is ink with a short lime bar on
 * top — the only lime in the dock; everything else stays gray.
 */

interface MobileTab {
  key: string;
  to: string;
  Icon: React.ElementType;
  label: string;
}

function Tab({ tab, active, badge }: { tab: MobileTab; active: boolean; badge?: number }) {
  const { Icon, label, to } = tab;
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      data-mobile-nav-item={tab.key}
      className="relative flex min-w-0 flex-1 flex-col items-center justify-center gap-1 py-1 text-[11px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      style={{
        color: active ? M.ink : M.ink3,
        fontWeight: active ? 600 : 500,
      }}
    >
      {active && (
        <span
          data-mobile-nav-indicator
          aria-hidden
          className="absolute left-1/2 top-0 h-[3px] w-7 -translate-x-1/2 rounded-b-[3px]"
          style={{ background: M.primary }}
        />
      )}
      <span className="relative flex-shrink-0">
        <Icon size={21} strokeWidth={active ? 2.25 : 1.75} aria-hidden />
        {badge != null && badge > 0 && (
          <span
            data-mobile-nav-badge
            className="absolute -right-2 -top-1.5 inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-full px-1 text-[8.5px] font-bold leading-none text-white"
            style={{ background: M.redBright, border: '1.5px solid #fff' }}
          >
            {badge}
          </span>
        )}
      </span>
      <span className="truncate">{label}</span>
    </Link>
  );
}

export function MobileNav({ onMore, onCreate }: { onMore: () => void; onCreate: () => void }) {
  const { t } = useTranslation();
  const { groups, isFetched } = useStudioNavGroups();
  const createActions = useCreateActions();

  if (!isFetched) return null;

  const byRoute = new Map(groups.flatMap((g) => g.items).map((i) => [i.to, i]));

  const defs: MobileTab[] = [
    { key: 'home', to: '/app/analytics', Icon: House, label: t('nav.home', 'Home') },
    { key: 'tests', to: '/app/tests', Icon: ClipboardList, label: t('nav.tests', 'Tests') },
    { key: 'ai', to: '/app/documents', Icon: Sparkles, label: t('nav.ai', 'AI') },
  ];
  const visible = defs.filter((d) => byRoute.has(d.to));
  // Screens reached through "Yana" (question bank, subjects, history, library…) mark it current.
  const moreActive = !visible.some((d) => byRoute.get(d.to)?.active);

  return (
    <nav
      aria-label={t('primary_navigation', 'Primary navigation')}
      className="pointer-events-auto absolute inset-x-0 bottom-0 z-[25] flex items-stretch border-t bg-white"
      style={{
        height: 'calc(64px + env(safe-area-inset-bottom))',
        paddingBottom: 'env(safe-area-inset-bottom)',
        background: M.card,
        borderColor: M.border,
      }}
    >
      {visible.slice(0, 2).map((d) => {
        const item = byRoute.get(d.to)!;
        return <Tab key={d.key} tab={d} active={item.active} badge={item.badge} />;
      })}

      {createActions.length > 0 && (
        <button
          type="button"
          onClick={onCreate}
          aria-label={t('create', 'Create')}
          data-mobile-nav-item="create"
          className="flex min-w-0 flex-1 flex-col items-center justify-center gap-1 py-1 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          style={{ color: M.ink3 }}
        >
          <SquarePlus size={21} strokeWidth={1.75} aria-hidden />
          <span className="truncate">{t('create', 'Create')}</span>
        </button>
      )}

      {visible.slice(2).map((d) => {
        const item = byRoute.get(d.to)!;
        return <Tab key={d.key} tab={d} active={item.active} badge={item.badge} />;
      })}

      <button
        type="button"
        onClick={onMore}
        aria-label={t('more', 'More')}
        aria-current={moreActive ? 'page' : undefined}
        data-mobile-nav-item="more"
        className="relative flex min-w-0 flex-1 flex-col items-center justify-center gap-1 py-1 text-[11px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        style={{ color: moreActive ? M.ink : M.ink3, fontWeight: moreActive ? 600 : 500 }}
      >
        {moreActive && (
          <span
            data-mobile-nav-indicator
            aria-hidden
            className="absolute left-1/2 top-0 h-[3px] w-7 -translate-x-1/2 rounded-b-[3px]"
            style={{ background: M.primary }}
          />
        )}
        <Ellipsis size={21} strokeWidth={moreActive ? 2.25 : 1.75} aria-hidden />
        <span className="truncate">{t('more', 'More')}</span>
      </button>
    </nav>
  );
}

export default MobileNav;
